import type { AbsentReason, ArtifactState, RecordingId, SessionId } from "@don/wire";

/**
 * One row of the owner's library, and every derivation the screen makes from it.
 *
 * All of this is pure and none of it is in a component: the library's product rules (which
 * day group a session falls in, what the compact expiry chip says in each of its three
 * states, which level a row's expiry marker is at) are the things most worth asserting, and a rule
 * that only exists inside a render function can only be asserted through a browser.
 *
 * **This is not `wire::SessionSummary`.** The field list that used to stand here is gone
 * deliberately: it was a completeness claim about a generated type that nothing compared it
 * to, and it had already been edited twice to stay true. Read `@don/wire`'s
 * `SessionSummary` for what the API answers. What matters here is the difference: the
 * library's column set also needs a person's name, a shape, a title, the Summary's state and
 * the video's expiry date, and the API answers none of those on this path today. The gap is
 * enumerated in this issue's plan and note rather than papered over here.
 */
export interface LibraryRow {
  /**
   * The row's one key: a legacy session's id, or a recording's id where `recordingId` is set
   * (issue 321, `backend/adapter.ts` `libraryRowsOf`).
   */
  readonly sessionId: SessionId;
  /** Set on a recording's row (issue 321): the recording it opens. Unset on a legacy session. */
  readonly recordingId?: RecordingId;
  /**
   * A recording's segments in ordinal order, each a session's own row (issue 321), so a link
   * that names a segment (`/sessions/{id}`) still finds the session it names. Unset on a legacy
   * session.
   */
  readonly segments?: readonly LibraryRow[];
  /** Who did the work. Never a device. */
  readonly person: string;
  readonly startedAtMs: number;
  /**
   * When the session ended, or `null` while it has not. Issue 18's Decision 11 is why this
   * is nullable rather than assumed: closing the grace window writes the share gate and
   * nothing else, so a session's end time is the session loop's to write (issue 38).
   */
  readonly endedAtMs: number | null;
  /** Row-scale shape label, e.g. `3 screens`, `1 screen`, `stills`. */
  readonly shape: string;
  /**
   * Auto-written from the Summary, so a session nobody spoke during has none. `null` is
   * the absent-by-design case and is common, not an error.
   */
  readonly title: string | null;
  /** The Summary's state. One of exactly four, and they must not look alike. */
  readonly summaryState: ArtifactState;
  /**
   * Which absence an `absent_by_design` Summary is, `wire::SessionSummary`'s
   * `summary_absent_reason` (issue 85): one of `wire::AbsentReason`'s spellings, or `null`
   * where the server names none. `SummaryCell` gives the build limit and no recording their
   * own words and reads any other as the bare absence.
   */
  readonly summaryAbsentReason: AbsentReason | null;
  readonly noteCount: number;
  /** When the video is deleted. Renewable; the derived text outlives it (decisions §(a)). */
  readonly videoExpiresAtMs: number;
  /** When the transcript, Summary and Observations are deleted. Renewal does not move it. */
  readonly derivedExpiresAtMs: number;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * The three-letter months, written out.
 *
 * `Intl`'s own short month is not stable enough to render a product's copy from: `en-GB`
 * abbreviates September as `Sept`, `en-US` as `Sep`, and which of the two a runtime gives
 * back depends on its ICU version rather than on anything this code says. So the table is
 * here and the locale decides nothing; `Intl` is asked for numbers only.
 */
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/** The three-letter weekdays, Sunday first as `Date#getUTCDay` counts them. Same reason. */
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/**
 * Calendar and clock fields in one time zone, as numbers.
 *
 * `undefined` is the viewer's own zone, which is what every screen passes; the tests pass
 * a named one so a date assertion means the same thing on every machine.
 */
function fieldsAt(atMs: number, timeZone?: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(atMs);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  const year = read("year");
  const monthIndex = read("month") - 1;
  const day = read("day");
  const midnightUtc = Date.UTC(year, monthIndex, day);
  return {
    year,
    monthIndex,
    month: MONTHS[monthIndex] ?? "",
    day,
    weekday: WEEKDAYS[new Date(midnightUtc).getUTCDay()] ?? "",
    // `% 24` because a runtime may answer 24 for midnight even under `h23`.
    hour: read("hour") % 24,
    minute: read("minute"),
    // The calendar day as a whole number, so "yesterday" is a subtraction in the viewer's
    // calendar rather than 24 hours back from now, which is wrong across a DST change.
    dayNumber: Math.round(midnightUtc / 86_400_000),
  };
}

/** The full month names, for day-group labels. Same reason as `MONTHS`. */
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/**
 * Which day group a session started in (design-spec §4 "Day group", issue 193): `Today`,
 * `Yesterday`, `Earlier this week`, `Last week`, then the month name, with the year when it is
 * not `nowMs`'s. Weeks start Sunday. Days are the viewer's calendar days in `timeZone`, the
 * calendar `startedLabel` uses, so a row's group and its Started value cannot disagree.
 */
export function dayGroupLabel(nowMs: number, atMs: number, timeZone?: string): string {
  const now = fieldsAt(nowMs, timeZone);
  const at = fieldsAt(atMs, timeZone);
  const back = now.dayNumber - at.dayNumber;
  if (back <= 0) return "Today";
  if (back === 1) return "Yesterday";
  // Days since this week's Sunday; `dayNumber` 0 (1 Jan 1970) was a Thursday.
  const intoWeek = (now.dayNumber + 4) % 7;
  if (back <= intoWeek) return "Earlier this week";
  if (back <= intoWeek + 7) return "Last week";
  const month = MONTH_NAMES[at.monthIndex] ?? "";
  return at.year === now.year ? month : `${month} ${at.year}`;
}

export interface DayGroup {
  readonly label: string;
  readonly rows: readonly LibraryRow[];
}

/**
 * The feed's order: newest first, cut into day groups (issue 193). The sort-by-expiry order
 * is gone with its control; a group's label only ever moves further back in time down the
 * list, so consecutive rows with one label are the whole group.
 */
export function groupByDay(
  nowMs: number,
  rows: readonly LibraryRow[],
  timeZone?: string,
): DayGroup[] {
  const groups: { label: string; rows: LibraryRow[] }[] = [];
  for (const row of newestFirst(rows)) {
    const label = dayGroupLabel(nowMs, row.startedAtMs, timeZone);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.label === label) last.rows.push(row);
    else groups.push({ label, rows: [row] });
  }
  return groups;
}

/** Newest first; ties on the session id so a re-render cannot reshuffle equals. */
export function newestFirst(rows: readonly LibraryRow[]): LibraryRow[] {
  return [...rows].sort(
    (a, b) => b.startedAtMs - a.startedAtMs || a.sessionId.localeCompare(b.sessionId),
  );
}

/**
 * `4:10 pm`: US 12-hour, lowercase am/pm (decision 34).
 *
 * Midnight is `12:00 am` and noon `12:00 pm`. Built from numbers rather than from `Intl`'s
 * `hour12` output, which writes `PM`, may put a narrow no-break space before it, and
 * varies by ICU version.
 */
export function clockLabel(atMs: number, timeZone?: string): string {
  const at = fieldsAt(atMs, timeZone);
  const hour = at.hour % 12 === 0 ? 12 : at.hour % 12;
  return `${hour}:${String(at.minute).padStart(2, "0")} ${at.hour < 12 ? "am" : "pm"}`;
}

/** `Mon Sep 28, 4:00 pm`: a moment with its day, for any line that is not today-relative. */
export function dateTimeLabel(atMs: number, timeZone?: string): string {
  const at = fieldsAt(atMs, timeZone);
  return `${at.weekday} ${at.month} ${at.day}, ${clockLabel(atMs, timeZone)}`;
}

/**
 * The Started column: the clock time alone for today and yesterday, else the full form.
 *
 * Today and yesterday are the viewer's calendar days, not 24 and 48 hours back from now.
 */
export function startedLabel(nowMs: number, atMs: number, timeZone?: string): string {
  const back = fieldsAt(nowMs, timeZone).dayNumber - fieldsAt(atMs, timeZone).dayNumber;
  return back === 0 || back === 1 ? clockLabel(atMs, timeZone) : dateTimeLabel(atMs, timeZone);
}

/**
 * `Oct 12` in the current year, `Sep 2, 2027` outside it.
 *
 * "Current" is `nowMs`'s year in the viewer's zone, so a derived term a year out carries
 * its year and a date this year does not.
 */
export function longDateLabel(nowMs: number, atMs: number, timeZone?: string): string {
  const at = fieldsAt(atMs, timeZone);
  const sameYear = at.year === fieldsAt(nowMs, timeZone).year;
  return `${at.month} ${at.day}${sameYear ? "" : `, ${at.year}`}`;
}

/** `Sep 12, 2026`: the date with its year, always (the Team table's Recordings, issue 212). */
export function calendarDateLabel(atMs: number, timeZone?: string): string {
  const at = fieldsAt(atMs, timeZone);
  return `${at.month} ${at.day}, ${at.year}`;
}

/**
 * `48 min` or `1h 07m`, and `null` for a session with no end time.
 *
 * A length, never a clock time. The em dash a reader sees in that column is a placeholder
 * glyph, not this function's job.
 */
export function durationLabel(row: LibraryRow): string | null {
  if (row.endedAtMs === null) return null;
  const minutes = Math.max(0, Math.round((row.endedAtMs - row.startedAtMs) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * What a session is called on every screen: its title, else `Sep 30 '26 - Maricel Santos`.
 *
 * The title is written from the Summary, so a session nobody spoke during has none, and a
 * build that derives no Summary (issue 85) writes none for any session. Both used to read
 * as a sentence about the absence ("Untitled session", "Title not generated in this
 * build"), which on the deployed Console made every row read the same. The date and person are what an owner tells sessions apart by, so a missing title
 * reads as those (decision 31). Which absence it is stays the Summary cell's to say (P5).
 *
 * The date is the recording's start in the viewer's zone, the calendar the Started column
 * uses.
 */
export function sessionTitle(row: LibraryRow, timeZone?: string): string {
  if (row.title !== null && row.title !== "") return row.title;
  const at = fieldsAt(row.startedAtMs, timeZone);
  return `${at.month} ${at.day} '${String(at.year).slice(2)} - ${row.person}`;
}

/** Which of the four D20 treatments a row's Video cell gets (design-spec §4 "Expiry marker"). */
export type ExpiryLevel = "none" | "soon" | "urgent" | "gone";

export interface ExpiryMarker {
  readonly level: ExpiryLevel;
  /** `Video deleted in 8 days`, `Video deleted`; empty for `none`. The cell's tooltip. */
  readonly text: string;
  /** What the row's Video cell reads: `Deleted in 8 days`, `Video deleted`. */
  readonly short: string;
  /** `urgent` only: the sentence the expanded red marker shows. */
  readonly detail?: string;
}

/** At this many days or fewer the row shows the hourglass (D20). */
export const SOON_DAYS = 10;
/** At this many days or fewer the marker is red and expands (D20, design-spec §2.1 `--problem`). */
export const URGENT_DAYS = 5;

/**
 * The row's expiry marker (D20, PRD module 6): level and words from **one floored count**, so
 * 10.9 days reads `10 days` and is `soon`, and the level can never disagree with the number.
 *
 * Floored, never rounded: a countdown to an irreversible deletion may read early, never late
 * (P18). Under one day the count is hours, and its floor has a floor of 1: `0 hours` while the
 * video still exists is the one reading P18 forbids outright, so the final hour reads `1 hour`
 * (as the chip this replaces did).
 */
export function expiryMarker(nowMs: number, row: LibraryRow, timeZone?: string): ExpiryMarker {
  const remaining = row.videoExpiresAtMs - nowMs;
  if (remaining <= 0) return { level: "gone", text: "Video deleted", short: "Video deleted" };
  const days = Math.floor(remaining / DAY_MS);
  if (days > SOON_DAYS) return { level: "none", text: "", short: "" };
  let count: string;
  if (days >= 1) count = `${days} ${days === 1 ? "day" : "days"}`;
  else {
    const hours = Math.max(1, Math.floor(remaining / HOUR_MS));
    count = `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  const short = `Deleted in ${count}`;
  const text = `Video deleted in ${count}`;
  if (days > URGENT_DAYS) return { level: "soon", text, short };
  const on = fieldsAt(row.videoExpiresAtMs, timeZone);
  return {
    level: "urgent",
    text,
    short,
    detail:
      `The video is deleted on ${on.weekday} ${on.month} ${on.day}. The transcript, summary ` +
      `and knowledge stay until ${longDateLabel(nowMs, row.derivedExpiresAtMs, timeZone)}.`,
  };
}

/** A calendar day, and how a line names it (`Oct 30`). */
export interface CalendarDay {
  readonly year: number;
  readonly monthIndex: number;
  readonly day: number;
  readonly label: string;
}

/**
 * `Video until Oct 30` (issue 201's meta line): the latest calendar day that **ends** at or
 * before the deletion, so the day named is one the whole of which the video is still there.
 * That is always the day before the deletion's own day (its midnight is at or before the
 * deletion, even a deletion at midnight exactly). Counted by calendar day in the zone, never
 * 24 hours back, which lands two days back across a spring-forward night.
 */
export function videoUntil(deletionMs: number, timeZone?: string): CalendarDay {
  const at = fieldsAt(deletionMs, timeZone);
  const before = new Date(Date.UTC(at.year, at.monthIndex, at.day - 1));
  const year = before.getUTCFullYear();
  const monthIndex = before.getUTCMonth();
  const day = before.getUTCDate();
  return { year, monthIndex, day, label: `${MONTHS[monthIndex] ?? ""} ${day}` };
}

/** The session meta line's expiry (issue 201): the row marker's level and the line's words. */
export interface MetaExpiry {
  readonly level: ExpiryLevel;
  /** `Video until Oct 30`, or `Video deleted Oct 3` once gone. */
  readonly text: string;
}

/**
 * The session meta line's expiry (D22, PRD story 33): `Video until <videoUntil>` at the level
 * {@link expiryMarker} gives the row, so the 10 and 5 day treatments read the same on the row and
 * on the session; once the video is gone, the day it went.
 */
export function metaExpiry(nowMs: number, row: LibraryRow, timeZone?: string): MetaExpiry {
  const level = expiryMarker(nowMs, row, timeZone).level;
  if (level === "gone") {
    return { level, text: `Video deleted ${longDateLabel(nowMs, row.videoExpiresAtMs, timeZone)}` };
  }
  return { level, text: `Video until ${videoUntil(row.videoExpiresAtMs, timeZone).label}` };
}

/**
 * The session-detail treatment for a session whose video has gone, per decisions §(a), in the
 * screen catalog's words (`session-video-deleted`, issue 203).
 *
 * **It is the aged-out treatment, not an error and not a broken player.** Video is kept on
 * a renewable 30-day term and the derived text on its own one-year term, so a session with
 * a transcript and no video is a real and common state rather than a half-loaded page. The
 * screen says which date took the video and which date the writing is kept until, because
 * the owner's next question after "where is the video" is "what is left".
 *
 * It lives beside `expiryMarker` rather than in the session module because it is the same
 * rule at a different size: `Video deleted` in the library row and this on the detail, both
 * decided by `videoExpiresAtMs` against the same clock.
 */
export function videoIsGone(nowMs: number, row: LibraryRow): boolean {
  return row.videoExpiresAtMs <= nowMs;
}

export interface AgedOut {
  readonly heading: string;
  readonly body: string;
}

export function agedOutCopy(nowMs: number, row: LibraryRow, timeZone?: string): AgedOut {
  return {
    heading: `Video deleted on ${longDateLabel(nowMs, row.videoExpiresAtMs, timeZone)}.`,
    body: `The summary, knowledge, transcript and notes stay until ${longDateLabel(
      nowMs,
      row.derivedExpiresAtMs,
      timeZone,
    )}.`,
  };
}
